import React, { useEffect, useState } from 'react';
import { API_BASED_URL } from '../config.js';

const SupervisorManageBooking = () => {
  const [bookings, setBookings] = useState([]);
  const [status, setStatus] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const token = localStorage.getItem('userToken');

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const params = new URLSearchParams();
      if (status) params.append('status', status);
      if (from) {
        // Convert date input (YYYY-MM-DD) to proper format for backend
        params.append('from', from);
      }
      if (to) {
        // Convert date input (YYYY-MM-DD) to proper format for backend
        params.append('to', to);
      }
      const res = await fetch(`${API_BASED_URL}api/supervisor/bookings?${params.toString()}`, { headers: { Authorization: token } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load');
      setBookings(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); // eslint-disable-next-line
  }, []);

  const act = async (booking_id, action) => {
    if (action !== 'approve' && !window.confirm(`Confirm ${action} booking #${booking_id}?`)) return;
    try {
      const res = await fetch(`${API_BASED_URL}api/supervisor/bookings/${booking_id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ action })
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to update');
      await load();
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">Manage Bookings</h2>
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select value={status} onChange={(e)=>setStatus(e.target.value)} className="w-full border rounded px-3 py-2">
              <option value="">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input 
              type="date" 
              value={from} 
              onChange={(e)=>setFrom(e.target.value)} 
              className="w-full border rounded px-3 py-2" 
              max={to || new Date().toISOString().split('T')[0]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input 
              type="date" 
              value={to} 
              onChange={(e)=>setTo(e.target.value)} 
              className="w-full border rounded px-3 py-2"
              min={from}
              max={new Date().toISOString().split('T')[0]}
            />
          </div>
          <button onClick={load} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded px-4 py-2 transition-colors">
            Apply Filter
          </button>
          <button 
            onClick={()=>{setStatus('');setFrom('');setTo('');load();}} 
            className="border border-gray-300 hover:bg-gray-50 text-gray-700 rounded px-4 py-2 transition-colors"
          >
            Clear All
          </button>
        </div>
      </div>

      {error && <div className="text-red-600 mb-3">{error}</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Booking #</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">User</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Facility</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Booking Date</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Created At</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Selected Slots</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Cost</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {bookings.map(b => (
                <tr key={b.booking_id} className="hover:bg-gray-50">
                  <td className="px-4 py-2">{b.booking_id}</td>
                  <td className="px-4 py-2">{b.full_name} ({b.email})</td>
                  <td className="px-4 py-2">{b.facility_name}</td>
                  <td className="px-4 py-2">{formatDate(b.booking_date)}</td>
                  <td className="px-4 py-2">
                    <div className="text-sm">
                      {b.created_at ? (
                        <span className="text-gray-600">
                          {formatDateTime(b.created_at)}
                        </span>
                      ) : (
                        <span className="text-gray-500 text-xs">N/A</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="text-sm">
                      {b.selected_slots ? (
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">
                          {b.selected_slots}
                        </span>
                      ) : (
                        <span className="text-gray-500 text-xs">No slots info</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2">₹{b.cost}</td>
                  <td className="px-4 py-2">{b.status}</td>
                  <td className="px-4 py-2">
                    {/* Show approve/reject buttons for all statuses */}
                    <div className="flex flex-col space-y-2">
                      <button 
                        onClick={()=>act(b.booking_id,'approve')} 
                        className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                          b.status === 'Approved' 
                            ? 'bg-gray-400 cursor-not-allowed hover:scale-100' 
                            : 'bg-green-600 hover:bg-green-700 focus:ring-green-500 shadow-sm'
                        }`}
                        disabled={b.status === 'Approved'}
                      >
                        {b.status === 'Approved' ? '✓ Approved' : '✓ Approve'}
                      </button>
                      <button 
                        onClick={()=>act(b.booking_id,'reject')} 
                        className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 shadow-sm"
                      >
                        ✗ Reject
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SupervisorManageBooking;
